import { Phone } from 'lucide-react';
import { useState } from 'react';
import { useLocation } from 'react-router-dom';

export default function NavLinks() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  const scrollToSection = (e: React.MouseEvent, sectionId: string) => {
    e.preventDefault();
    if (location.pathname !== '/') {
      window.location.href = `/#${sectionId}`;
    } else {
      document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMenuOpen(false);
  };

  const handleKeyPress = (e: React.KeyboardEvent, sectionId: string) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      scrollToSection(e as unknown as React.MouseEvent, sectionId);
    }
  };

  return (
    <div role="navigation" aria-label="Main navigation">
      {/* Desktop Navigation */}
      <div 
        className="hidden md:flex items-center space-x-8 flex-wrap"
        role="menubar"
        aria-label="Desktop navigation menu"
      >
        <a 
          href="#home" 
          onClick={(e) => scrollToSection(e, 'home')}
          onKeyPress={(e) => handleKeyPress(e, 'home')}
          className="text-gray-700 hover:text-primary-600 font-bold px-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-600 focus:ring-offset-2"
          role="menuitem"
          aria-label="Navigate to home section"
          tabIndex={0}
        >
          Home
        </a>
        <a 
          href="#services" 
          onClick={(e) => scrollToSection(e, 'services')}
          onKeyPress={(e) => handleKeyPress(e, 'services')}
          className="text-gray-700 hover:text-primary-600 font-bold px-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-600 focus:ring-offset-2"
          role="menuitem"
          aria-label="Navigate to services section"
          tabIndex={0}
        >
          Services
        </a>
        <a 
          href="#doctors" 
          onClick={(e) => scrollToSection(e, 'doctors')}
          onKeyPress={(e) => handleKeyPress(e, 'doctors')}
          className="text-gray-700 hover:text-primary-600 font-bold px-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-600 focus:ring-offset-2"
          role="menuitem"
          aria-label="Navigate to our team section"
          tabIndex={0}
        >
          Our Team
        </a>
        <a 
          href="https://mycw245.ecwcloud.com/portal28445" 
          target="_blank"
          rel="noopener noreferrer"
          className="text-gray-700 hover:text-primary-600 font-bold px-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-600 focus:ring-offset-2"
          role="menuitem"
          aria-label="Access Patient Portal"
          tabIndex={0}
        >
          Patient Portal
        </a>
        <a 
          href="https://www.healowpay.com/"          
          target="_blank"
          rel="noopener noreferrer"
          className="text-gray-700 hover:text-primary-600 font-bold px-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-600 focus:ring-offset-2"
          role="menuitem"
          aria-label="Pay Your Bill"
          tabIndex={0}
        >
          Pay Your Bill
        </a>
        <a 
          href="#contact" 
          onClick={(e) => scrollToSection(e, 'contact')}
          onKeyPress={(e) => handleKeyPress(e, 'contact')}
          className="text-gray-700 hover:text-primary-600 font-bold px-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-600 focus:ring-offset-2"
          role="menuitem"
          aria-label="Navigate to contact section"
          tabIndex={0}
        >
          Contact Us
        </a>
      </div>

      {/* Mobile Navigation */}
      <div className="md:hidden">
        <button
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          className="text-gray-700 p-2 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-600 focus:ring-offset-2"
          aria-expanded={isMenuOpen}
          aria-label={isMenuOpen ? "Close navigation menu" : "Open navigation menu"}
          aria-controls="mobile-menu"
        >
          <svg
            className="h-6 w-6"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            {isMenuOpen ? (
              <path d="M6 18L18 6M6 6l12 12" />
            ) : (
              <path d="M4 6h16M4 12h16M4 18h16" />
            )}
          </svg>
        </button>

        {/* Mobile Menu */}
        <div 
          id="mobile-menu"
          className={`absolute top-16 left-0 right-0 bg-white shadow-md ${isMenuOpen ? 'block' : 'hidden'}`}
          role="menu"
          aria-label="Mobile navigation menu"
          aria-hidden={!isMenuOpen}
        >
          <div 
            className="px-2 pt-2 pb-3 space-y-3"
            role="menubar"
            aria-orientation="vertical"
          >
            {[
              { id: 'home', label: 'Home' },
              { id: 'services', label: 'Services' },
              { id: 'doctors', label: 'Our Team' },
              { id: 'portal', label: 'Patient Portal', href: 'https://mycw245.ecwcloud.com/portal28445', external: true },
              { id: 'payment', label: 'Pay Your Bill', href: 'https://www.healowpay.com/', external: true },
              { id: 'contact', label: 'Contact Us' }
            ].map(item => (
              <a 
                key={item.id}
                href={item.href || `#${item.id}`}
                onClick={(e) => item.external ? null : scrollToSection(e, item.id)}
                onKeyPress={(e) => item.external ? null : handleKeyPress(e, item.id)}
                target={item.external ? "_blank" : undefined}
                rel={item.external ? "noopener noreferrer" : undefined}
                className="block w-full text-left px-3 py-2 text-gray-700 hover:text-primary-600 font-bold rounded-md focus:outline-none focus:ring-2 focus:ring-primary-600 focus:ring-offset-2"
                role="menuitem"
                aria-label={`Navigate to ${item.label.toLowerCase()}`}
                tabIndex={isMenuOpen ? 0 : -1}
              >
                {item.label}
              </a>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
} 